import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connectorAPI } from '../../utils/api';
import {
  faTimes,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faExclamationCircle,
  faCloud,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const Microsoft365ConnectorForm = ({ isOpen, onClose, onSave, editingConnector = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    clientId: '',
    clientSecret: '',
    tenantId: '',
    userEmail: '',
    redirectUri: '',
    refreshToken: ''
  });

  const [showSensitive, setShowSensitive] = useState({
    clientSecret: false,
    refreshToken: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  // UUID regex pattern
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Populate form data when editing
  useEffect(() => {
    if (editingConnector && isOpen) {
      const credentials = editingConnector.credentials || {};
      setFormData({
        name: editingConnector.name || '',
        clientId: credentials.client_id || '',
        clientSecret: credentials.client_secret || '',
        tenantId: credentials.tenant_id || '',
        userEmail: credentials.user_email || '',
        redirectUri: credentials.redirect_uri || '',
        refreshToken: credentials.refresh_token || ''
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        clientId: '',
        clientSecret: '',
        tenantId: '',
        userEmail: '',
        redirectUri: '',
        refreshToken: ''
      });
    }
  }, [editingConnector, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.name.trim()) newErrors.name = 'Connection name is required';
    if (!formData.clientId.trim()) newErrors.clientId = 'Client ID is required';
    if (!formData.clientSecret.trim()) newErrors.clientSecret = 'Client Secret is required';
    if (!formData.tenantId.trim()) newErrors.tenantId = 'Tenant ID is required';

    // Format validation
    if (formData.name && formData.name.length < 3) {
      newErrors.name = 'Connection name must be at least 3 characters';
    }

    if (formData.clientId && !uuidPattern.test(formData.clientId)) {
      newErrors.clientId = 'Client ID must be a valid UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)';
    }

    if (formData.tenantId && !uuidPattern.test(formData.tenantId)) {
      newErrors.tenantId = 'Tenant ID must be a valid UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)';
    }

    // Email validation (optional field)
    if (formData.userEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
      newErrors.userEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const toggleSensitive = (field) => {
    setShowSensitive(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before testing');
      return;
    }

    setTestingConnection(true);
    try {
      // First save/update the connector if editing
      let connectorName = formData.name;

      if (editingConnector) {
        // Update existing connector for testing
        const connectorData = {
          name: formData.name,
          service_type: 'microsoft365',
          credentials: {
            client_id: formData.clientId,
            client_secret: formData.clientSecret,
            tenant_id: formData.tenantId,
            user_email: formData.userEmail || null,
            redirect_uri: formData.redirectUri || null,
            refresh_token: formData.refreshToken || null
          }
        };
        await connectorAPI.updateConnector(editingConnector.name, connectorData);
        connectorName = editingConnector.name;
      }

      // Test the connection
      const result = await connectorAPI.testConnection(connectorName);

      if (result.success) {
        toast.success(result.message || 'Microsoft 365 connection test successful!');
      } else {
        toast.error(result.message || 'Connection test failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      const errorMessage = error.response?.data?.detail?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Connection test failed';
      toast.error(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix all errors before saving');
      return;
    }

    setIsLoading(true);
    try {
      const connectorData = {
        name: formData.name,
        service_type: 'microsoft365',
        credentials: {
          client_id: formData.clientId,
          client_secret: formData.clientSecret,
          tenant_id: formData.tenantId,
          user_email: formData.userEmail || null,
          redirect_uri: formData.redirectUri || null,
          refresh_token: formData.refreshToken || null
        }
      };

      await onSave(connectorData);
      toast.success(editingConnector ? 'Microsoft 365 connector updated successfully!' : 'Microsoft 365 connector created successfully!');
      handleClose();
    } catch (error) {
      toast.error('Failed to create connector');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      clientId: '',
      clientSecret: '',
      tenantId: '',
      userEmail: '',
      redirectUri: '',
      refreshToken: ''
    });
    setErrors({});
    setShowSensitive({
      clientSecret: false,
      refreshToken: false
    });
    onClose();
  };

  const renderField = (field, label, required = true, options = {}) => {
    const isSensitive = ['clientSecret', 'refreshToken'].includes(field);
    const inputType = isSensitive && !showSensitive[field] ? 'password' : 'text';

    return (
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            type={inputType}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className={`form-input w-full ${errors[field] ? 'border-red-500' : ''} ${isSensitive ? 'pr-10' : ''}`}
            placeholder={options.placeholder || `Enter ${label.toLowerCase()}`}
            {...options}
          />
          {isSensitive && (
            <button
              type="button"
              onClick={() => toggleSensitive(field)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <FontAwesomeIcon
                icon={showSensitive[field] ? faEyeSlash : faEye}
                className="text-sm"
              />
            </button>
          )}
        </div>
        {errors[field] && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-500 text-sm flex items-center"
          >
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
            {errors[field]}
          </motion.p>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center text-white">
                  <FontAwesomeIcon icon={faCloud} className="text-lg" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {editingConnector ? 'Edit Microsoft 365 Connector' : 'Microsoft 365 Connector'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {editingConnector ? 'Update Azure AD authentication for Microsoft 365' : 'Configure Azure AD authentication for Microsoft 365'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Connection Name */}
            {renderField('name', 'Connection Name', true, {
              placeholder: 'e.g., Microsoft 365 Production'
            })}

            {/* Client ID */}
            {renderField('clientId', 'Client ID (Application ID)', true, {
              placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
            })}

            {/* Client Secret */}
            {renderField('clientSecret', 'Client Secret', true, {
              placeholder: 'Enter client secret'
            })}

            {/* Tenant ID */}
            {renderField('tenantId', 'Tenant ID (Directory ID)', true, {
              placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
            })}

            {/* User Email (Optional) */}
            {renderField('userEmail', 'User Email (Optional)', false, {
              placeholder: 'user@company.com'
            })}
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-4">
              Used for user-delegated access scenarios
            </p>

            {/* Redirect URI (Optional) */}
            {renderField('redirectUri', 'Redirect URI (Optional)', false, {
              placeholder: 'Registered OAuth redirect URI for this Azure app'
            })}
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-4">
              Must match a redirect URI registered on the Azure app. Leave blank to use the default.
            </p>

            {/* Refresh Token (Optional) */}
            {renderField('refreshToken', 'Refresh Token (Optional)', false, {
              placeholder: 'Generated by Test Connection sign-in, or paste manually'
            })}
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-4">
              Stored encrypted. Leave blank to use app-only (client-credentials) auth instead.
            </p>

            {/* Helper Text */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                How to get Microsoft Azure credentials:
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>1. Go to Azure Portal</li>
                <li>2. Navigate to Azure Active Directory → App registrations</li>
                <li>3. Create new registration or select existing app</li>
                <li>4. Copy Application (client) ID and Directory (tenant) ID</li>
                <li>5. Go to Certificates & secrets → New client secret</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between">
              <button
                onClick={handleTestConnection}
                disabled={testingConnection}
                className={`btn ${testingConnection ? 'btn-disabled' : 'btn-secondary'}`}
              >
                {testingConnection ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    Test Connection
                  </>
                )}
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'}`}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      {editingConnector ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingConnector ? 'Update Connector' : 'Create Connector'
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Microsoft365ConnectorForm;
